import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {Skeleton} from 'primeng/skeleton';
import {ButtonIconComponent} from '../../../../../shared/components/button-icon/button-icon.component';
import {DialogBase} from '../../../../../shared/components/dialogs/dialog-base/dialog-base';
import {InputItemSelection} from '../../../../../shared/components/input-item-selection/input-item-selection';
import {TranslateSafePipe} from '../../../../../shared/pipes/translation.pipe';
import {AlarmRuleTriggerType} from '../../../../../types/dtos/alarm-rule-trigger.dto';
import {InputItemSelectInterface} from '../../../../../types/interfaces/input.interface';
import {BaseRuleTrigger} from './rule-triggers/base-rule-trigger';

@Component({
  selector: 'app-alarm-rule-triggers',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonIconComponent,
    DialogBase,
    InputItemSelection,
    Skeleton,
    TranslateSafePipe,
  ],
  templateUrl: './alarm-rule-triggers.html',
  styleUrl: './alarm-rule-triggers.css',
})
export class AlarmRuleTriggers {
  @Input()
  mode: 'create' | 'edit' | 'view' = 'view';

  @Input()
  public formGroup!: FormGroup;

  @Input()
  public isLoading: boolean = false;

  public addDialogOpen: boolean = false;
  public createGroup: FormGroup = new FormGroup({
    type: new FormControl(),
  });
  public TypeSelect: InputItemSelectInterface = {
    formGroup: this.createGroup,
    isTranslated: true,
    label: 'Trigger Type',
    required: true,
    id: 'type',
    options: [],
  };

  private _triggerDefinitions!: Record<AlarmRuleTriggerType, BaseRuleTrigger>;

  @Input({required: true})
  public set triggerDefinitions(value: Record<AlarmRuleTriggerType, BaseRuleTrigger>) {
    this._triggerDefinitions = value;
    this.TypeSelect.options = Object.values(AlarmRuleTriggerType)
      .map((type) => {
        let definition: BaseRuleTrigger = this._triggerDefinitions[type];

        return {
          isTranslated: true,
          label: definition?.getName() ?? 'Unknown',
          value: type,
          description: definition.getDescription() ?? '',
          icon: definition.getIcon() ?? 'question_mark',
        };
      });
  }

  public get triggers(): FormArray<FormGroup> {
    return this.formGroup.get('triggers') as FormArray<FormGroup>;
  }

  public createTriggerOnClick(): void {
    this.addDialogOpen = true;
  }

  public onTypeSelect(type: string): void {
    this.addDialogOpen = false;

    this.addTrigger(type as AlarmRuleTriggerType);
  }

  public addTrigger(type: AlarmRuleTriggerType): void {
    this.triggers.push(this._triggerDefinitions[type].createFormGroup({
      type,
      config: {},
    }));
  }

  public removeTrigger(index: number): void {
    this.triggers.removeAt(index);
  }

  public getTriggerType(index: number): AlarmRuleTriggerType | null {
    return this.triggers.at(index)
      .get('type')?.value;
  }

  public getDefinition(type: AlarmRuleTriggerType): any {
    return this._triggerDefinitions[type];
  }

  public getName(index: number): string {
    let type: AlarmRuleTriggerType | null = this.getTriggerType(index);
    if (!type) return '';
    return this._triggerDefinitions[type]?.getName() ?? 'Unknown';
  }

  public getIcon(index: number): string {
    let type: AlarmRuleTriggerType | null = this.getTriggerType(index);
    if (!type) return '';
    return this._triggerDefinitions[type]?.getIcon() ?? 'question_mark';
  }
}
